import { z } from 'zod';
/**
 * BE-7: the steps of one SDD artifact generation run (spec, tech plan, tests, tasks), stored per
 * attempt in `platform.sdd_generation_attempt_progress` and returned in the artifact bundles.
 */
export const GENERATION_PROGRESS_PHASES = [
    'context',
    'exploration',
    'drafting',
    'validation_repair',
    'ai_review',
    'saving',
];
/** Newest entries are kept; a run rarely has more than ~10. */
export const GENERATION_PROGRESS_MAX_ENTRIES = 20;
/** Phases the gateways enter; ignored once the runtime has started repairing or reviewing. */
const GATEWAY_PHASES = new Set(['exploration', 'drafting']);
const AFTER_DRAFT_PHASES = new Set([
    'validation_repair',
    'ai_review',
]);
/** Validator codes are static identifiers; anything else is not stored. */
const PROGRESS_CODE_PATTERN = /^[a-z0-9_.:-]{1,64}$/;
const generationProgressPhaseSchema = z.enum(GENERATION_PROGRESS_PHASES);
export const generationProgressEntrySchema = z.object({
    phase: generationProgressPhaseSchema,
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().optional(),
    round: z.number().int().positive().optional(),
    /** Internal (validator code); stored for analysis, never returned by the API. */
    code: z.string().regex(PROGRESS_CODE_PATTERN).optional(),
});
export const generationProgressStoredSchema = z.object({
    v: z.literal(1),
    phases: z.array(generationProgressEntrySchema).max(GENERATION_PROGRESS_MAX_ENTRIES),
});
/** API shape: enum, round and timestamps only. */
export const generationProgressDtoSchema = z.object({
    current: generationProgressPhaseSchema.nullable(),
    round: z.number().int().positive().nullable(),
    phases: z.array(z.object({
        phase: generationProgressPhaseSchema,
        startedAt: z.string(),
        endedAt: z.string().nullable(),
    })),
});
export function emptyGenerationProgress() {
    return { v: 1, phases: [] };
}
function appendEntry(state, entry, now) {
    const closed = closeLast(state.phases, now);
    const phases = [...closed, entry].slice(-GENERATION_PROGRESS_MAX_ENTRIES);
    return { v: 1, phases };
}
function closeLast(phases, now) {
    const nowIso = now.toISOString();
    return phases.map((p, i) => i === phases.length - 1 && p.endedAt === undefined ? { ...p, endedAt: nowIso } : p);
}
/**
 * Returns the state with `phase` as the current entry (the previous one closed). Unchanged (same
 * object) when the phase and round are already current, or when a gateway phase arrives after the
 * runtime started repairing or reviewing (a repair turn is not a new exploration or draft).
 */
export function enterGenerationPhase(state, input, now) {
    if (GATEWAY_PHASES.has(input.phase) &&
        state.phases.some((p) => AFTER_DRAFT_PHASES.has(p.phase))) {
        return state;
    }
    const last = state.phases.at(-1);
    if (last && last.phase === input.phase && last.round === input.round) {
        return state;
    }
    if (input.replaceExploration && last?.phase === 'exploration' && last.endedAt === undefined) {
        const replaced = { phase: input.phase, startedAt: last.startedAt };
        return { v: 1, phases: [...state.phases.slice(0, -1), replaced] };
    }
    let start = now;
    if (input.startedAt !== undefined) {
        const lastStartMs = last ? new Date(last.startedAt).getTime() : Number.NEGATIVE_INFINITY;
        const ms = input.startedAt.getTime();
        if (ms > lastStartMs && ms < now.getTime()) {
            start = input.startedAt;
        }
    }
    const entry = { phase: input.phase, startedAt: start.toISOString() };
    if (input.round !== undefined && Number.isInteger(input.round) && input.round > 0) {
        entry.round = input.round;
    }
    if (input.code !== undefined && PROGRESS_CODE_PATTERN.test(input.code)) {
        entry.code = input.code;
    }
    return appendEntry(state, entry, start);
}
/** The run is over: the open last entry ends now. Unchanged (same object) when none is open. */
export function closeCurrentGenerationPhase(state, now) {
    const last = state.phases.at(-1);
    if (!last || last.endedAt !== undefined) {
        return state;
    }
    return { v: 1, phases: closeLast(state.phases, now) };
}
/** A retried model call (e.g. after a timeout): closes the current entry and opens it again. */
export function restartCurrentGenerationPhase(state, now) {
    const last = state.phases.at(-1);
    if (!last) {
        return state;
    }
    const entry = { phase: last.phase, startedAt: now.toISOString() };
    if (last.round !== undefined)
        entry.round = last.round;
    if (last.code !== undefined)
        entry.code = last.code;
    return appendEntry(state, entry, now);
}
/**
 * Maps a stored value to the API shape. Null for attempts without progress and for values that do
 * not parse. For an attempt that is no longer running, the open last entry ends at the attempt's
 * last transition (`updatedAt`, clamped to the entry start for clock skew between services).
 */
export function toGenerationProgressDto(raw, attempt) {
    if (raw === null || raw === undefined) {
        return null;
    }
    const parsed = generationProgressStoredSchema.safeParse(raw);
    if (!parsed.success || parsed.data.phases.length === 0) {
        return null;
    }
    const phases = parsed.data.phases;
    const running = attempt.lifecycleState === 'running';
    const updatedAtMs = new Date(attempt.updatedAt).getTime();
    const last = phases[phases.length - 1];
    return {
        current: last.phase,
        round: last.round ?? null,
        phases: phases.map((p, i) => {
            let endedAt = p.endedAt ?? null;
            if (endedAt === null && i === phases.length - 1 && !running && Number.isFinite(updatedAtMs)) {
                endedAt = new Date(Math.max(new Date(p.startedAt).getTime(), updatedAtMs)).toISOString();
            }
            return { phase: p.phase, startedAt: p.startedAt, endedAt };
        }),
    };
}
//# sourceMappingURL=generation-progress.js.map